import React, { useEffect, useMemo, useState } from 'react';
import PatientForm from '../components/PatientForm';
import {
  useCreatePatient,
  usePatients,
  Patient,
  usePatientNotes,
  useAddPatientNote,
  usePatientPrescriptions,
  usePatientLabResults,
  usePatientAttachments,
  useCreatePrescription,
  useCreateLabResult,
  useUploadAttachment,
} from '../services/api';
import { PatientFormData } from '../types/forms';
import useMockDataToast from '../hooks/useMockDataToast';
import { useAuth } from '../state/AuthContext';
import { useToasts } from '../state/ToastContext';

const careFlags = [
  { title: 'Pain scoring', owner: 'Dr. Ramos', due: '17:00 today', severity: 'High' },
  { title: 'Biopsy review', owner: 'Dr. Patel', due: 'Tomorrow 09:00', severity: 'Medium' },
  { title: 'Owner callback', owner: 'Front desk', due: 'Within 2 hrs', severity: 'High' },
];

const automationQueue = [
  { label: 'Vaccine booster reminders', status: '26 scheduled', channel: 'SMS + email' },
  { label: 'Post-op instructions', status: '11 sent', channel: 'Owner portal' },
  { label: 'Medication refill', status: '5 pending', channel: 'App notification' },
];

const noteCategories = ['Rounds', 'Nursing', 'Client', 'Pharmacy', 'General'];

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Patients: React.FC = () => {
  const [query, setQuery] = useState('');
  const [speciesFilter, setSpeciesFilter] = useState('all');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [noteCategory, setNoteCategory] = useState<string>(noteCategories[0]);
  const [noteBody, setNoteBody] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [prescriptionDraft, setPrescriptionDraft] = useState({
    medication: '',
    dosage: '',
    frequency: '',
    duration: '',
    instructions: '',
  });
  const [labDraft, setLabDraft] = useState({
    testName: '',
    status: 'Pending',
    findings: '',
    performedAt: '',
  });
  const [attachmentDraft, setAttachmentDraft] = useState<{
    filename: string;
    contentType?: string;
    contentBase64?: string;
  }>({
    filename: '',
  });
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  const {
    data: patients = [],
    isLoading,
    isFallback,
    fallbackMessage,
  } = usePatients();
  useMockDataToast(isFallback, 'Live patient registry unreachable. Showing demo roster.', fallbackMessage);
  const createPatient = useCreatePatient();
  const [formError, setFormError] = useState<string | null>(null);
  const patientIdentifier = selectedPatient?.id?.toString();
  const {
    data: patientNotes = [],
    isFallback: notesFallback,
    fallbackMessage: notesFallbackMessage,
    isLoading: notesLoading,
  } = usePatientNotes(patientIdentifier);
  useMockDataToast(
    notesFallback,
    'Patient notes service unreachable. Showing cached timeline.',
    notesFallbackMessage,
  );
  const addPatientNote = useAddPatientNote(patientIdentifier);
  const {
    data: prescriptions = [],
    isFallback: prescriptionsFallback,
    fallbackMessage: prescriptionsFallbackMessage,
  } = usePatientPrescriptions(patientIdentifier);
  useMockDataToast(
    prescriptionsFallback,
    'Prescription service unreachable. Showing cached orders.',
    prescriptionsFallbackMessage,
  );
  const {
    data: labResults = [],
    isFallback: labsFallback,
    fallbackMessage: labsFallbackMessage,
  } = usePatientLabResults(patientIdentifier);
  useMockDataToast(
    labsFallback,
    'Lab service unreachable. Showing cached results.',
    labsFallbackMessage,
  );
  const {
    data: attachments = [],
    isFallback: attachmentsFallback,
    fallbackMessage: attachmentsFallbackMessage,
  } = usePatientAttachments(patientIdentifier);
  useMockDataToast(
    attachmentsFallback,
    'Attachment store unreachable. Showing cached files.',
    attachmentsFallbackMessage,
  );
  const createPrescription = useCreatePrescription(patientIdentifier);
  const createLabResult = useCreateLabResult(patientIdentifier);
  const uploadAttachment = useUploadAttachment(patientIdentifier);
  const { pushToast } = useToasts();
  const { hasPermission } = useAuth();
  const canWriteNotes = hasPermission('notes:write');

  const filteredPatients = useMemo(() => {
    return patients.filter((patient) => {
      const matchesQuery = patient.name.toLowerCase().includes(query.toLowerCase());
      const matchesSpecies =
        speciesFilter === 'all' || patient.species.toLowerCase() === speciesFilter;
      return matchesQuery && matchesSpecies;
    });
  }, [patients, query, speciesFilter]);

  useEffect(() => {
    if (!selectedPatient && filteredPatients.length > 0) {
      setSelectedPatient(filteredPatients[0]);
    }
  }, [filteredPatients, selectedPatient]);

  const handleCreatePatient = async (formData: PatientFormData) => {
    setFormError(null);
    try {
      const savedPatient = await createPatient.mutateAsync(formData);
      setSelectedPatient(savedPatient);
      setShowModal(false);
    } catch {
      setFormError('Unable to save patient. Please try again.');
    }
  };

  const handleAddNote = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNoteError(null);
    if (!selectedPatient) return;
    if (!canWriteNotes) {
      setNoteError('Your role cannot add clinical notes. Ask a medical lead to escalate access.');
      return;
    }
    if (noteBody.trim().length === 0) return;
    try {
      await addPatientNote.mutateAsync({
        note: noteBody.trim(),
        category: noteCategory,
        author: 'Care Team',
        role: 'Staff',
      });
      setNoteBody('');
    } catch (error) {
      console.error('Failed to add patient note', error);
      setNoteError('Unable to save note right now.');
    }
  };

  const fileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result?.toString() ?? '';
        const [, base64] = result.split(',');
        resolve(base64 ?? result);
      };
      reader.onerror = () => reject(reader.error ?? new Error('Unable to read file'));
      reader.readAsDataURL(file);
    });

  const handleCreatePrescription = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPatient || !prescriptionDraft.medication.trim()) {
      pushToast({ tone: 'warning', message: 'Medication name is required' });
      return;
    }
    try {
      await createPrescription.mutateAsync({
        medication: prescriptionDraft.medication.trim(),
        dosage: prescriptionDraft.dosage,
        frequency: prescriptionDraft.frequency,
        duration: prescriptionDraft.duration,
        instructions: prescriptionDraft.instructions,
      });
      setPrescriptionDraft({
        medication: '',
        dosage: '',
        frequency: '',
        duration: '',
        instructions: '',
      });
      pushToast({ tone: 'success', message: 'Prescription added to chart' });
    } catch (error) {
      pushToast({
        tone: 'error',
        message: 'Unable to add prescription',
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const handleCreateLabResult = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPatient || !labDraft.testName.trim()) {
      pushToast({ tone: 'warning', message: 'Test name is required' });
      return;
    }
    try {
      await createLabResult.mutateAsync({
        testName: labDraft.testName.trim(),
        status: labDraft.status,
        findings: labDraft.findings,
        performedAt: labDraft.performedAt || null,
      });
      setLabDraft({ testName: '', status: 'Pending', findings: '', performedAt: '' });
      pushToast({ tone: 'success', message: 'Lab result added' });
    } catch (error) {
      pushToast({
        tone: 'error',
        message: 'Unable to add lab result',
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const handleAttachmentFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await fileToBase64(file);
      setAttachmentDraft({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64: base64,
      });
      setAttachmentError(null);
    } catch (error) {
      setAttachmentError('Unable to read file');
    }
  };

  const handleUploadAttachment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPatient) return;
    if (!attachmentDraft.filename || !attachmentDraft.contentBase64) {
      setAttachmentError('Select a file to upload');
      return;
    }
    try {
      await uploadAttachment.mutateAsync({
        filename: attachmentDraft.filename,
        contentType: attachmentDraft.contentType,
        contentBase64: attachmentDraft.contentBase64,
      });
      setAttachmentDraft({ filename: '' });
      setAttachmentError(null);
      pushToast({ tone: 'success', message: 'Attachment uploaded' });
    } catch (error) {
      setAttachmentError('Unable to upload attachment');
      pushToast({
        tone: 'error',
        message: 'Unable to upload attachment',
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="section-title mb-1">Patient registry</p>
          <h1 className="text-2xl font-semibold text-slate-900">Patients</h1>
          <p className="text-sm text-slate-500">
            Track medical history, treatment plans, and caregiver communication.
          </p>
        </div>
        <div className="flex flex-1 flex-col gap-3 md:flex-row md:flex-none">
          <input
            type="search"
            placeholder="Search by patient or owner"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm focus:border-brand-blue focus:ring-brand-blue"
          />
          <div className="flex gap-2">
            {['all', 'canine', 'feline', 'exotic'].map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setSpeciesFilter(option)}
                className={`rounded-xl px-3 py-2 text-xs font-semibold capitalize ${
                  speciesFilter === option
                    ? 'bg-brand-blue text-white'
                    : 'bg-white text-slate-500 shadow-sm'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="rounded-xl bg-brand-blue px-5 py-2 text-sm font-semibold text-white shadow-soft"
            onClick={() => setShowModal(true)}
          >
            + New patient
          </button>
        </div>
      </div>

      <div className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] table-auto divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-6 py-3">Patient</th>
                <th className="px-6 py-3">Species</th>
                <th className="px-6 py-3">Age</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-slate-400">
                    Loading patient roster...
                  </td>
                </tr>
              ) : filteredPatients.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-slate-400">
                    No patients found.
                  </td>
                </tr>
              ) : (
                filteredPatients.map((patient) => (
                  <tr
                    key={patient.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setSelectedPatient(patient)}
                  >
                    <td className="px-6 py-4 font-medium text-slate-900">{patient.name}</td>
                    <td className="px-6 py-4 capitalize">{patient.species}</td>
                    <td className="px-6 py-4">{patient.age} yrs</td>
                    <td className="px-6 py-4">
                      <span className="pill bg-emerald-50 text-emerald-600">Active</span>
                    </td>
                    <td className="px-6 py-4 text-right text-xs text-brand-blue">View chart</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="glass-panel space-y-4 p-6 lg:col-span-2">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Care team focus</h3>
            <p className="mt-2 text-sm text-slate-500">
              Centralize escalations so triage, surgery, and client services stay aligned.
            </p>
          </div>
          <ul className="space-y-3 text-sm">
            {careFlags.map((flag) => (
              <li
                key={flag.title}
                className="flex items-center justify-between rounded-2xl border border-slate-100 bg-white px-4 py-3"
              >
                <div>
                  <p className="font-semibold text-slate-900">{flag.title}</p>
                  <p className="text-xs text-slate-500">{flag.owner}</p>
                </div>
                <span
                  className={`pill ${
                    flag.severity === 'High'
                      ? 'bg-rose-50 text-rose-600'
                      : 'bg-amber-50 text-amber-600'
                  }`}
                >
                  {flag.due}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="glass-panel space-y-4 p-6">
          {selectedPatient ? (
            <>
              <p className="section-title mb-1">Selected patient</p>
              <h3 className="text-xl font-semibold text-slate-900">{selectedPatient.name}</h3>
              <p className="text-sm text-slate-500">
                {selectedPatient.species}, {selectedPatient.age} yrs
              </p>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-slate-500">Caregiver</dt>
                  <dd className="font-semibold">{selectedPatient.owner ?? 'N/A'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Last visit</dt>
                  <dd className="font-semibold">{selectedPatient.lastVisit ?? 'Mar 3, 2025'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Primary doctor</dt>
                  <dd className="font-semibold">{selectedPatient.doctor ?? 'Dr. Avery'}</dd>
                </div>
              </dl>
            </>
          ) : (
            <p className="text-sm text-slate-500">Select a patient to preview details.</p>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="glass-panel space-y-3 p-6">
          <div>
            <p className="section-title mb-1">Automation</p>
            <h3 className="text-lg font-semibold text-slate-900">Reminder queue</h3>
          </div>
          <ul className="space-y-2 text-sm">
            {automationQueue.map((item) => (
              <li
                key={item.label}
                className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
              >
                <p className="font-semibold text-slate-900">{item.label}</p>
                <p className="text-xs text-slate-500">{item.channel}</p>
                <p className="text-xs font-semibold text-brand-blue">{item.status}</p>
              </li>
            ))}
          </ul>
        </div>
        <div className="glass-panel space-y-4 p-6 lg:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="section-title mb-1">Clinical documentation</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedPatient ? `${selectedPatient.name} 繚 team notes` : 'Select a patient'}
              </h3>
            </div>
            {selectedPatient && (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                {patientNotes.length} entries
              </span>
            )}
          </div>

          {selectedPatient ? (
            <>
              {notesLoading ? (
                <p className="text-sm text-slate-500">Loading notes timeline...</p>
              ) : patientNotes.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No documented notes yet. Capture the first update below.
                </p>
              ) : (
                <ul className="space-y-3 text-sm">
                  {patientNotes.map((note) => (
                    <li
                      key={note.id}
                      className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-slate-700"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{note.category}</p>
                          <p className="text-xs text-slate-500">
                            {note.author} 繚 {note.role}
                          </p>
                        </div>
                        <span className="text-[11px] text-slate-400">
                          {new Date(note.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <p className="mt-2 text-slate-700">{note.note}</p>
                    </li>
                  ))}
                </ul>
              )}

              <form className="space-y-3 pt-2" onSubmit={handleAddNote}>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="text-xs font-semibold text-slate-500">
                    Category
                    <select
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brand-blue focus:ring-brand-blue disabled:bg-slate-100"
                      value={noteCategory}
                      onChange={(event) => setNoteCategory(event.target.value)}
                      disabled={!canWriteNotes}
                    >
                      {noteCategories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs font-semibold text-slate-500">
                    Visibility
                    <input
                      disabled
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-500"
                      value="Internal care team"
                    />
                  </label>
                </div>
                <textarea
                  className="min-h-[140px] w-full rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue disabled:bg-slate-100"
                  placeholder="Document SOAP updates, client communication, or shift handoffs."
                  value={noteBody}
                  onChange={(event) => setNoteBody(event.target.value)}
                  disabled={!canWriteNotes}
                />
                {!canWriteNotes && (
                  <p className="rounded-xl bg-amber-50 px-4 py-2 text-xs font-semibold text-amber-800">
                    Your role can review notes but needs medical leadership access to author updates.
                  </p>
                )}
                {noteError && (
                  <p className="text-xs font-semibold text-rose-600">
                    {noteError}
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 disabled:opacity-50"
                    onClick={() => setNoteBody('')}
                    disabled={!canWriteNotes}
                  >
                    Clear
                  </button>
                  <button
                    type="submit"
                    disabled={
                      !canWriteNotes || addPatientNote.isPending || noteBody.trim().length === 0
                    }
                    className="rounded-xl bg-brand-blue px-4 py-2 text-xs font-semibold text-white shadow-soft disabled:opacity-50"
                  >
                    {addPatientNote.isPending ? 'Saving...' : 'Add note'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <p className="text-sm text-slate-500">Select a patient to review documentation.</p>
          )}
        </div>
      </div>

      {selectedPatient && (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="glass-panel space-y-3 p-6">
            <div>
              <p className="section-title mb-1">Medications</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedPatient.name} · prescriptions
              </h3>
            </div>
            {prescriptions.length === 0 ? (
              <p className="text-sm text-slate-500">No prescriptions recorded.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {prescriptions.map((rx) => (
                  <li
                    key={rx.id}
                    className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-slate-700"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-900">{rx.medication}</p>
                      <span className="text-[11px] text-slate-400">
                        {new Date(rx.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {rx.dosage} {rx.frequency ? `· ${rx.frequency}` : ''}
                    </p>
                    {rx.instructions && (
                      <p className="mt-1 text-xs text-slate-500">{rx.instructions}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <form className="space-y-2 pt-2" onSubmit={handleCreatePrescription}>
              <input
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Medication"
                value={prescriptionDraft.medication}
                onChange={(event) =>
                  setPrescriptionDraft((prev) => ({ ...prev, medication: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              <div className="grid gap-2 md:grid-cols-2">
                <input
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Dosage"
                  value={prescriptionDraft.dosage}
                  onChange={(event) =>
                    setPrescriptionDraft((prev) => ({ ...prev, dosage: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                />
                <input
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Frequency"
                  value={prescriptionDraft.frequency}
                  onChange={(event) =>
                    setPrescriptionDraft((prev) => ({ ...prev, frequency: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                />
              </div>
              <input
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Duration"
                value={prescriptionDraft.duration}
                onChange={(event) =>
                  setPrescriptionDraft((prev) => ({ ...prev, duration: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              <textarea
                className="min-h-[80px] rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Instructions"
                value={prescriptionDraft.instructions}
                onChange={(event) =>
                  setPrescriptionDraft((prev) => ({ ...prev, instructions: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              <button
                type="submit"
                disabled={!canWriteNotes || createPrescription.isPending || !prescriptionDraft.medication}
                className="w-full rounded-xl bg-brand-blue px-4 py-2 text-xs font-semibold text-white shadow-soft disabled:cursor-not-allowed disabled:opacity-50"
              >
                {createPrescription.isPending ? 'Saving…' : 'Add prescription'}
              </button>
            </form>
          </div>

          <div className="glass-panel space-y-3 p-6">
            <div>
              <p className="section-title mb-1">Lab results</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedPatient.name} · diagnostics
              </h3>
            </div>
            {labResults.length === 0 ? (
              <p className="text-sm text-slate-500">No lab work recorded.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {labResults.map((lab) => (
                  <li
                    key={lab.id}
                    className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-slate-700"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-900">{lab.testName}</p>
                      <span className="text-[11px] text-slate-400">
                        {new Date(lab.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">Status: {lab.status}</p>
                    {lab.findings && (
                      <p className="mt-1 text-xs text-slate-500">{lab.findings}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <form className="space-y-2 pt-2" onSubmit={handleCreateLabResult}>
              <input
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Test name"
                value={labDraft.testName}
                onChange={(event) =>
                  setLabDraft((prev) => ({ ...prev, testName: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              <input
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="Findings / summary"
                value={labDraft.findings}
                onChange={(event) =>
                  setLabDraft((prev) => ({ ...prev, findings: event.target.value }))
                }
                disabled={!canWriteNotes}
              />
              <div className="grid gap-2 md:grid-cols-2">
                <select
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={labDraft.status}
                  onChange={(event) =>
                    setLabDraft((prev) => ({ ...prev, status: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                >
                  {['Pending', 'In progress', 'Completed'].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <input
                  type="datetime-local"
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={labDraft.performedAt}
                  onChange={(event) =>
                    setLabDraft((prev) => ({ ...prev, performedAt: event.target.value }))
                  }
                  disabled={!canWriteNotes}
                />
              </div>
              <button
                type="submit"
                disabled={!canWriteNotes || createLabResult.isPending || !labDraft.testName}
                className="w-full rounded-xl bg-brand-indigo px-4 py-2 text-xs font-semibold text-white shadow-soft disabled:cursor-not-allowed disabled:opacity-50"
              >
                {createLabResult.isPending ? 'Saving…' : 'Add lab result'}
              </button>
            </form>
          </div>

          <div className="glass-panel space-y-3 p-6">
            <div>
              <p className="section-title mb-1">Attachments</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedPatient.name} · files
              </h3>
            </div>
            {attachments.length === 0 ? (
              <p className="text-sm text-slate-500">No files uploaded yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {attachments.map((file) => {
                  const href =
                    file.contentBase64 && file.contentType
                      ? `data:${file.contentType};base64,${file.contentBase64}`
                      : undefined;
                  return (
                    <li
                      key={file.id}
                      className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-slate-700"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-slate-900">{file.filename}</p>
                        <span className="text-[11px] text-slate-400">
                          {formatFileSize(file.sizeBytes)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">{file.contentType}</p>
                      {href && (
                        <a
                          href={href}
                          download={file.filename}
                          className="text-xs font-semibold text-brand-blue"
                        >
                          Download
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            <form className="space-y-2 pt-2" onSubmit={handleUploadAttachment}>
              <input
                type="file"
                className="w-full text-xs text-slate-500 file:mr-3 file:rounded-full file:border-0 file:bg-brand-blue/10 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-brand-blue disabled:opacity-50"
                onChange={handleAttachmentFile}
                disabled={!canWriteNotes}
              />
              {attachmentDraft.filename && (
                <p className="text-xs text-slate-500">
                  Ready to upload: {attachmentDraft.filename}
                </p>
              )}
              {attachmentError && (
                <p className="text-xs font-semibold text-rose-600">{attachmentError}</p>
              )}
              <button
                type="submit"
                disabled={
                  !canWriteNotes ||
                  uploadAttachment.isPending ||
                  !attachmentDraft.filename ||
                  !attachmentDraft.contentBase64
                }
                className="w-full rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-soft disabled:cursor-not-allowed disabled:opacity-50"
              >
                {uploadAttachment.isPending ? 'Uploading…' : 'Upload attachment'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
          <div className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-soft">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <p className="section-title mb-1">Add patient</p>
                <h3 className="text-lg font-semibold text-slate-900">Create medical record</h3>
              </div>
              <button
                type="button"
                className="text-sm font-semibold text-slate-400 hover:text-slate-900"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
            <div className="pt-4">
              {formError && (
                <div className="mb-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                  {formError}
                </div>
              )}
              <PatientForm onSubmit={handleCreatePatient} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Patients;
